import React, { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { List } from 'immutable';
import { makeStyles, Typography } from '@material-ui/core';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import { HEADER_ACTIONS_OPTION } from 'constants/table.constants';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { formatNumberToPrice } from 'common/utils';
import { PeakModules } from 'common/constants/peakModules';
import commonMessages from 'common/messages/messages';
import { IPaymentImt } from 'common/components/PersonProfile/interfaces';
import { IBodyCell, ICellAlign, IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import { IFilterSettings } from 'common/interfaces/filter';
import { IPageMetaImt } from 'common/interfaces/pagination';
import { ActionItem, ActionsMenu, Table } from 'common/components/index';
import StatusCell from 'modules/reports/components/ReportsTable/StatusCell';
import InvoiceDetailsModal from '../InvoiceDetailsModal/InvoiceDetailsModal';
import tableHeaders from 'common/messages/tableHeaders';
import { InvoiceStatus } from 'common/interfaces/invoices';
import { IEmployeeBasicInfo } from 'interfaces/employee.interfaces';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import EmployeeLinkWithAvatar from 'common/components/ProfileLinkWithAvatar/EmployeeLinkWithAvatar';

const useStyles = makeStyles(() => ({
  invoiceNumberTitle: { cursor: 'pointer', display: 'inline-block' },
}));

interface IInvoicesTable {
  payments: List<IPaymentImt>;
  paymentsLoading: boolean;
  paymentsMeta?: IPageMetaImt;
  hideSearchInput?: boolean;
  hidePagination?: boolean;
  isExtendedView?: boolean;
  onChangeTableParams?: (tableParams: ITableParams) => void;
  setIsVoidModalOpen?: (invoiceId: string) => void;
  tableFilterSettings?: IFilterSettings[];
  tableParams?: ITableParams;
  disableSorting?: boolean;
  module: PeakModules;
  personId: number;
  hidePadding?: boolean;
  suppressFiltersMobilePadding?: boolean;
}

const InvoicesTable: FC<IInvoicesTable> = ({
  payments,
  paymentsLoading,
  paymentsMeta,
  hidePagination,
  hideSearchInput,
  isExtendedView,
  tableFilterSettings,
  onChangeTableParams,
  setIsVoidModalOpen,
  disableSorting,
  tableParams,
  module,
  personId,
  hidePadding = false,
  suppressFiltersMobilePadding = false,
}) => {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [timezoneMoment] = useTimezoneMoment();
  const classes = useStyles();
  const intl = useIntl();
  const { VOID_INVOICE } = usePermissionContext();

  const headerOptions: IHeadCell[] = [
    {
      id: 'number',
      label: <FormattedMessage {...tableHeaders.id} />,
      sort: !disableSorting,
    },
    {
      id: 'created_date',
      label: <FormattedMessage {...tableHeaders.created} />,
      sort: !disableSorting,
    },
    {
      id: 'salesperson',
      label: <FormattedMessage {...tableHeaders.salesperson} />,
      sort: !disableSorting,
    },
    {
      id: 'club',
      label: <FormattedMessage {...tableHeaders.club} />,
      sort: !disableSorting,
    },
    {
      id: 'register',
      label: <FormattedMessage {...tableHeaders.register} />,
      sort: !disableSorting,
    },
    ...(isExtendedView
      ? [
          {
            id: 'paid_date',
            label: <FormattedMessage {...tableHeaders.paid} />,
            sort: !disableSorting,
          },
          {
            id: 'subtotal',
            label: <FormattedMessage {...tableHeaders.subtotal} />,
            sort: !disableSorting,
            align: 'right' as ICellAlign,
          },
          {
            id: 'tax',
            label: <FormattedMessage {...tableHeaders.taxes} />,
            sort: !disableSorting,
            align: 'right' as ICellAlign,
          },
          {
            id: 'discount',
            label: <FormattedMessage {...tableHeaders.discount} />,
            sort: false,
            align: 'right' as ICellAlign,
          },
        ]
      : []),
    {
      id: 'total',
      label: <FormattedMessage {...tableHeaders.total} />,
      sort: !disableSorting,
      align: 'right',
    },
    ...(isExtendedView
      ? [
          {
            id: 'reward',
            label: <FormattedMessage {...tableHeaders.reward} />,
            sort: false,
          },
        ]
      : []),
    {
      id: 'status',
      label: <FormattedMessage {...tableHeaders.status} />,
      sort: !disableSorting,
      align: 'left',
    },
  ];

  if (isExtendedView && VOID_INVOICE) {
    headerOptions.push(HEADER_ACTIONS_OPTION);
  }

  const tableRows: ITableRow[] = payments
    .map(paymentItem => {
      const salesperson = paymentItem.get('salesperson');
      let employeeBasicInfo: IEmployeeBasicInfo | null = null;

      if (salesperson) {
        employeeBasicInfo = {
          id: salesperson.get('id'),
          firstName: salesperson.get('firstName'),
          lastName: salesperson.get('lastName'),
          imageUrl: salesperson.get('imageUrl'),
          employeeIdentifier: salesperson.get('employeeIdentifier'),
          active: salesperson.get('active'),
        };
      }

      const bodyCells: IBodyCell[] = [
        {
          label: '',
          className: 'body-cell',
          cellComponent: (
            <Typography
              variant="h6"
              color="primary"
              onClick={() => setSelectedInvoiceId(paymentItem.get('invoiceId'))}
              className={classes.invoiceNumberTitle}
            >
              {paymentItem.get('number')}
            </Typography>
          ),
        },
        {
          label: `${timezoneMoment(paymentItem.get('createdDate')).format(
            DEFAULT_DATE_TIME_FORMAT,
          )}`,
          className: 'body-cell',
        },
        {
          label: '',
          className: 'body-cell',
          cellComponent: employeeBasicInfo ? (
            <EmployeeLinkWithAvatar employee={employeeBasicInfo} />
          ) : null,
        },
        {
          label: paymentItem.getIn(['register', 'club', 'title']) || '',
          className: 'body-cell',
        },
        {
          label: `${paymentItem.getIn(['register', 'title'])}`,
          className: 'body-cell',
        },
        ...(isExtendedView
          ? [
              {
                label: `${timezoneMoment(paymentItem.get('paymentDate')).format(
                  DEFAULT_DATE_TIME_FORMAT,
                )}`,
                className: 'body-cell',
              },
              {
                label: `$${formatNumberToPrice(paymentItem.get('subtotalAmount'))}`,
                className: 'body-cell',
                align: 'right' as ICellAlign,
              },
              {
                label: `$${formatNumberToPrice(paymentItem.get('taxAmount'))}`,
                className: 'body-cell',
                align: 'right' as ICellAlign,
              },
              {
                label: `$${formatNumberToPrice(paymentItem.get('discountAmount'))}`,
                className: 'body-cell',
                align: 'right' as ICellAlign,
              },
            ]
          : []),
        {
          label: `$${formatNumberToPrice(paymentItem.get('totalAmount'))}`,
          className: 'body-cell',
          align: 'right',
        },
        ...(isExtendedView
          ? [
              {
                label: paymentItem.get('rewardAmount') ? `${paymentItem.get('rewardAmount')}` : '',
                className: 'body-cell',
              },
            ]
          : []),
        {
          label: '',
          className: 'body-cell',
          cellComponent: <StatusCell status={paymentItem.get('invoiceStatus')} />,
        },
      ];

      if (isExtendedView && VOID_INVOICE) {
        bodyCells.push({
          label: '',
          className: 'body-actions-cell',
          cellComponent: (
            <ActionsMenu horizontal tableActionsMode>
              <ActionItem
                disabled={paymentItem.get('invoiceStatus') !== InvoiceStatus.IN_PROGRESS}
                onClick={() => {
                  if (setIsVoidModalOpen) {
                    setIsVoidModalOpen(paymentItem.get('invoiceId'));
                  }
                }}
              >
                {intl.formatMessage(commonMessages.voidInvoice)}
              </ActionItem>
            </ActionsMenu>
          ),
        });
      }

      return {
        id: paymentItem.get('invoiceId'),
        cells: bodyCells,
      };
    })
    .toJS();

  return (
    <>
      <Table
        rows={tableRows}
        tableParams={tableParams}
        headerOptions={headerOptions}
        filters={tableFilterSettings}
        hideSearchInput={hideSearchInput}
        showPerPageSelect={!hidePagination}
        hidePagination={hidePagination}
        onChangeParams={onChangeTableParams}
        hideToolbar
        totalRows={paymentsMeta?.get('total')}
        page={paymentsMeta?.get('page')}
        isLoading={paymentsLoading}
        hidePadding={hidePadding}
        suppressFiltersMobilePadding={suppressFiltersMobilePadding}
      />

      {selectedInvoiceId ? (
        <InvoiceDetailsModal
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          module={module}
          personId={personId}
        />
      ) : null}
    </>
  );
};

export default InvoicesTable;
